/**
 * /field/ew/change-orders/:id — one change order on the crew's phone (EW-5,
 * GET /earthworks/field/change-orders/:id). SNAKE_CASE.
 *
 * Unlike a field ticket, a change order DOES carry its prices on the field
 * read: the customer's representative is approving extra money, so the amount
 * is the thing they sign for. While draft or sent the customer signs here
 * (name + signature); that approves it, the same as the emailed link would.
 */
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useEwFieldChangeOrder, useSignEwFieldChangeOrder } from '../../../hooks/useEwTickets';
import { useTenantTimeZone } from '../../../hooks/useTenantTimeZone';
import { ewInstant, ewRefusal, fmtMoney, fmtNumber } from '../../../lib/earthworks';
import { ewCoSignable, ewCoSkin, ewSignedVia } from '../../../lib/ew-tickets';
import {
  PtFieldBadge,
  PtFieldCard,
  PtFieldPage,
  PtFieldQueryState,
  PtFieldSectionTitle,
  PtRefusal,
} from '../../pressure-testing/field/PtFieldBits';
import { ewNotOnCrewMessage } from './EwFieldJobScreen';
import { EwSignForm } from './EwSignForm';

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-3 border-b border-fieldiq-border px-4 py-2.5 last:border-b-0">
      <span className="text-sm text-fieldiq-muted">{label}</span>
      <span className="text-right text-sm font-semibold text-white">{value}</span>
    </div>
  );
}

export default function EwFieldCoScreen() {
  const { id } = useParams<{ id: string }>();
  const tz = useTenantTimeZone();
  const query = useEwFieldChangeOrder(id);
  const sign = useSignEwFieldChangeOrder();
  const [error, setError] = useState<string | null>(null);
  const co = query.data ?? null;
  const refused = ewNotOnCrewMessage(query);
  const lines = co?.lines ?? [];
  const amount = co ? (co.status === 'approved' ? co.subtotal : co.lines_total) : null;

  return (
    <PtFieldPage title={co?.co_number || 'Change order'} backTo={co ? `/field/ew/jobs/${co.job_id}` : '/field/ew'} testId="ew-field-co">
      {refused ? (
        <PtRefusal message={refused} testId="ew-field-not-on-crew" />
      ) : (
        <PtFieldQueryState query={query} subject="this change order">
          {!co ? (
            <PtRefusal message="That change order no longer exists. It may have been voided or deleted." />
          ) : (
            <div className="space-y-4">
              <PtFieldCard>
                <div className="flex items-center justify-between gap-2 border-b border-fieldiq-border px-4 py-3">
                  <span className="text-base font-bold text-white">{co.customer_name || '—'}</span>
                  <PtFieldBadge skin={ewCoSkin(co.status)} />
                </div>
                <Row label="Job" value={co.job_number || '—'} />
                <Row label="Site" value={co.site_name || '—'} />
                <Row label="Amount" value={fmtMoney(amount, co.currency)} />
                <div className="px-4 py-3 text-sm text-fieldiq-text">
                  <p className="font-bold text-white">{co.reason}</p>
                  {co.description && <p className="mt-1 whitespace-pre-wrap">{co.description}</p>}
                </div>
              </PtFieldCard>

              <section>
                <PtFieldSectionTitle>Lines · {lines.length}</PtFieldSectionTitle>
                <PtFieldCard>
                  {lines.length === 0 ? (
                    <p className="px-4 py-3 text-sm text-fieldiq-muted">No lines yet. The office adds them before it can be signed.</p>
                  ) : (
                    lines.map((l) => (
                      <div key={l.id} data-testid="ew-field-co-line" className="flex justify-between gap-3 border-b border-fieldiq-border px-4 py-2.5 last:border-b-0">
                        <span className="min-w-0 text-sm">
                          <span className="block font-semibold text-white">{l.description || l.kind_label || '—'}</span>
                          <span className="block text-xs text-fieldiq-muted">{fmtNumber(l.quantity)} {l.unit_label || l.unit}</span>
                        </span>
                        <span className="shrink-0 font-mono text-sm text-white">{fmtMoney(l.amount, co.currency)}</span>
                      </div>
                    ))
                  )}
                </PtFieldCard>
              </section>

              {ewCoSignable(co.status) ? (
                <>
                  {error && <PtRefusal message={error} testId="ew-field-co-error" />}
                  <EwSignForm
                    heading="Customer approval"
                    intro={`Signing approves ${co.co_number} for ${fmtMoney(amount, co.currency)}.`}
                    submitLabel="Approve change order"
                    pending={sign.isPending}
                    error={null}
                    testId="ew-field-co-sign"
                    onSubmit={(body) => {
                      setError(null);
                      sign.mutate({ id: co.id, body }, {
                        onSuccess: () => toast.success(`${co.co_number} approved`),
                        onError: (err) => setError(ewRefusal(err, 'The change order could not be signed.')),
                      });
                    }}
                  />
                </>
              ) : (
                <PtFieldCard>
                  <Row label="Approved" value={co.approved_at ? `${ewSignedVia(co.approved_via)} · ${ewInstant(co.approved_at, tz)}` : '—'} />
                  <Row label="Signer" value={co.signer_name ? `${co.signer_name}${co.signer_title ? `, ${co.signer_title}` : ''}` : '—'} />
                  {co.signature_url && <div className="px-4 py-3"><img src={co.signature_url} alt="Signature" className="h-20 rounded bg-white p-1" /></div>}
                </PtFieldCard>
              )}
            </div>
          )}
        </PtFieldQueryState>
      )}
    </PtFieldPage>
  );
}
